/* Small shared pieces. Nothing here knows about jobs — it knows about pills,
 * buttons and toasts, so the screens can stay about the work. */

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { Decision, SubtaskStatus } from "../api/types";
import { MARK_GLYPH, markFor } from "../lib/verdict";

/* ----------------------------------------------------------------- labels */

/** THE EYEBROW. One atom, every caption in the app.
 *
 *  The prototype uses a single declaration — 10px / 800 / +.08em / uppercase /
 *  grey — in eight different components: TASKS, SUB-TASK, VERB, the section
 *  dividers, the table headers. That repetition is the strongest "designed by
 *  one person" signal in the whole file: your eye learns the shape once and
 *  then reads every screen faster, because tiny grey capitals always mean
 *  "this is the name of the thing below".
 *
 *  We had FIVE treatments for that one job — `text-xs font-semibold`,
 *  `text-[11px] font-semibold`, `text-[10px] uppercase tracking-wide`,
 *  `text-[11px] uppercase tracking-wider`, `text-[11px] tracking-widest` —
 *  which is what makes a screen read as assembled rather than designed.
 *
 *  SIZE IS OURS, NOT THE PROTOTYPE'S. Its eyebrow is 10px against 13px body
 *  text; ours is 12px against 15px, because testing asked for bigger type in
 *  the right pane on 17 Sep. The ratio is what carries the effect — 0.77 there,
 *  0.80 here — so keeping 12px is both faithful and what was asked for.
 *  Dropping to 10px to match the deck would quietly undo a tester's request.
 *
 *  `hint` is the second half of a two-part label: VERB · action, OBJECT ·
 *  acted on. It drops ALL THREE treatments — case, weight and tracking — so it
 *  recedes without shrinking, and the guideline ends up printed on the form
 *  instead of hidden in a tooltip. For an annotation tool whose product IS
 *  consistency between annotators, that is the spec at the point of use. */
export function Eyebrow(
  { children, hint, title, className = "", compact = false }:
  { children: ReactNode; hint?: string; title?: string; className?: string;
    /** 11px instead of 12px — the sub-task panel's field labels (testing,
     *  24 Sep: "decrease the label's font size slightly"). A prop rather than
     *  a className override: `text-xs` plus a second text size in one class
     *  string is decided by stylesheet order, not by which one came last. */
    compact?: boolean },
) {
  return (
    // `title` is carried because the Stepper's label owns the explanation for a
    // pinned boundary ("pinned to the task's start") — the control beside it is
    // disabled, and the label is the only thing left that can say why. Dropping
    // it while restyling would be a silent behaviour regression.
    <span title={title} className={`${compact ? "text-[11px]" : "text-xs"}
      font-extrabold uppercase tracking-[0.08em] text-muted ${className}`}>
      {children}
      {hint && (
        <span className="font-semibold normal-case tracking-normal text-hint">
          {" · "}{hint}
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ pills */

const STATUS_STYLE: Record<SubtaskStatus, string> = {
  APPROVED: "bg-verdict-approved-bg text-verdict-approved-ink ring-verdict-approved-line",
  REJECTED: "bg-verdict-rejected-bg text-verdict-rejected-ink ring-verdict-rejected-line",
  PENDING: "bg-ground text-text2 ring-line",
};

/** ✕, NOT ⛔. At pill size the no-entry sign renders as a filled circle with a
 *  horizontal bar through it, so "Rejected" looked prefixed with a MINUS —
 *  which reads as "removed" rather than "rejected". ✕ is the opposite of the ✓
 *  on the pill above it, which is the relationship the two labels have.
 *
 *  The same swap was made on the Reject button (see Inspector) and this pill
 *  was missed, so the two controls disagreed about what rejection looks like. */
const STATUS_LABEL: Record<SubtaskStatus, string> = {
  APPROVED: "✓ Approved", REJECTED: "✕ Rejected", PENDING: "Pending",
};

export function StatusPill({ status }: { status: SubtaskStatus }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs
      font-medium ring-1 ring-inset ${STATUS_STYLE[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

/** The judge's verdict, in the reviewer's words rather than the pipeline's.
 *
 *  THREE TREATMENTS, NOT TWO. This was `quiet = AUTO_ACCEPT` against one loud
 *  style for everything else, which drew "Check this" in alarm colours on 72%
 *  of the corpus — the RECHECK that means "LLM-k rewrote the label", i.e.
 *  settled work. The middle state now has its own quieter treatment and its own
 *  word: corrected, not "check this". lib/verdict.ts holds the rule.
 *
 *  AUTO_ACCEPT stays the quietest — it is the common case, and a loud badge on
 *  every clean sub-task trains the eye to ignore all of them. */
const DECISION_LABEL: Record<Decision, string> = {
  AUTO_ACCEPT: "AI confident",
  RECHECK: "Corrected by AI",
  HUMAN_REVIEW: "Needs a human",
  AUDIT: "Sampled for audit",
  REJECT: "AI rejected",
};

export function VerdictBadge(
  { decision, score }: { decision: Decision; score: number },
) {
  const mark = markFor({ decision, trust_score: score,
                         reason_codes: [], attribute_scores: {} });
  const quiet = mark === "confident";
  const corrected = mark === "corrected";
  return (
    <span
      title={`Trust score ${score.toFixed(2)}`}
      // A PILL IS ALWAYS A PILL. This badge was `rounded` (4px) while StatusPill
      // beside it was `rounded-full`, so two things with the same job and the
      // same size had different silhouettes. The prototype has one pill shape,
      // six colour families, no exceptions.
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]
        font-medium ring-1 ring-inset ${
          quiet
            ? "bg-ground text-muted ring-line"
            : corrected
              // Legible, but not in the alarm family. A correction is
              // information the reviewer should see, not a job on their list.
              ? "bg-ground text-text2 ring-line"
              : "bg-verdict-flagged-bg text-verdict-flagged-ink ring-verdict-flagged-line"
        }`}
    >
      {mark ? MARK_GLYPH[mark] : "○"} {DECISION_LABEL[decision]}
    </span>
  );
}

/* ---------------------------------------------------------------- buttons */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "approve" | "reject" | "danger";
};

const VARIANT: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary: "bg-teal text-white hover:bg-teal7",
  ghost: "border border-line bg-card text-ink hover:bg-ground",
  approve: "border border-verdict-approved-line bg-verdict-approved-bg text-verdict-approved-ink hover:bg-verdict-approved-line",
  reject: "bg-verdict-rejected-bg hover:bg-verdict-rejected-line text-verdict-rejected-ink ring-1 ring-verdict-rejected-line",
  danger: "bg-verdict-rejected text-white hover:bg-verdict-rejected-ink",
};

/** HOVER LIFTS, IT DOES NOT FLASH. The prototype's buttons answer the pointer
 *  with `border-color` plus a small shadow — the control rises a little. A
 *  background colour change is the cheaper reflex and it reads as a different
 *  button rather than the same button under your cursor.
 *
 *  `disabled:shadow-none` matters more than it looks: opacity alone leaves the
 *  elevation behind, so a dimmed control still sits proud of the page and still
 *  looks pressable. Taking the shadow away is what makes it read as inert. */
export function Button({ variant = "ghost", className = "", ...rest }: ButtonProps) {
  return (
    <button
      {...rest}
      className={`rounded-control px-3 py-1.5 text-sm font-medium
        transition duration-150 ease-settle hover:shadow-card
        disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none
        ${VARIANT[variant]} ${className}`}
    />
  );
}

/* ----------------------------------------------------------------- toasts */

interface Toast { id: number; text: string; tone: "info" | "error" }

const ToastCtx = createContext<(text: string, tone?: Toast["tone"]) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((text: string, tone: Toast["tone"] = "info") => {
    // Date.now() would collide when two toasts land in the same millisecond,
    // and React would then warn about duplicate keys. A counter cannot.
    const id = nextId++;
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 left-1/2 z-50
        flex -translate-x-1/2 flex-col items-center gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`pointer-events-auto max-w-lg rounded-well px-4 py-2 text-sm
              shadow-raised ring-1 ${
                t.tone === "error"
                  ? "bg-verdict-rejected text-white ring-verdict-rejected-ink"
                  : "bg-ground text-ink ring-line"
              }`}
          >
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

let nextId = 1;

/* ------------------------------------------------------------------ misc  */

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 text-sm text-muted">
      <span className="size-4 animate-spin rounded-full border-2
        border-line border-t-teal" />
      {label ?? "Loading…"}
    </div>
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-16 text-center">
      <p className="text-text2">{title}</p>
      {hint && <p className="max-w-md text-sm text-muted">{hint}</p>}
    </div>
  );
}
